import React, { useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, Table, Form, InputGroup, Button } from "react-bootstrap";

const PriceComparisonRow = ({ 
    formData, 
    details, 
    activeTab, 
    percentageValue, 
    amortizationTab, 
    amortizationPercentage,
    setFormData,
    setActiveTab,
    setPercentageValue,
    setIsEgenInsatsManuallyEdited
}) => {

    const [priceInterval, setPriceInterval] = useState(10000);

    const formatCurrency = (value) => {
        if (isNaN(value)) return value;
        return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
            .format(value)
            .replace("kr", "")
            .trim();
    };

    // Function to apply row settings to the main form
    const applyRowSettings = (row) => {
        // Update the purchase price
        setFormData(prevData => ({
            ...prevData,
            utgangspris: row.utgangspris
        }));

        // If we have a calculated down payment, determine if it's percentage-based or manual
        const calculatedPercentage = (row.egenInsats / row.utgangspris) * 100;
        const roundedPercentage = Math.round(calculatedPercentage);
        
        // Check if it's close to a common percentage (15%, 20%, 25%, etc.)
        const commonPercentages = [10, 15, 20, 25, 30, 35, 40, 45, 50];
        const isCommonPercentage = commonPercentages.some(pct => Math.abs(pct - calculatedPercentage) < 0.1);
        
        if (isCommonPercentage) {
            // Use percentage mode
            setActiveTab("percentage");
            setPercentageValue(roundedPercentage);
            setIsEgenInsatsManuallyEdited(false);
        } else {
            // Use manual amount mode
            setActiveTab("manual");
            setFormData(prevData => ({
                ...prevData,
                egenInsats: row.egenInsats
            }));
            setIsEgenInsatsManuallyEdited(true);
        }
    };

    const calculateForPrice = (utgangspris) => {
        // For villa: use driftkostnad, for bostadsrätt: use monthlyCost (avgift)
        const monthlyCost = parseFloat(formData.monthlyCost) || 0;
        const mortgageInterestRate = parseFloat(formData.mortgageInterestRate) / 100 || 0;
        const taxeringsvarde = parseFloat(formData.taxeringsvarde) || 0;
        const pantbrev = parseFloat(formData.pantbrev) || 0;

        // Calculate down payment based on current tab selection
        const egenInsats = activeTab === "percentage"
            ? (utgangspris * (parseFloat(percentageValue) || 0)) / 100
            : parseFloat(formData.egenInsats) || (utgangspris * 0.15);

        const remainingAmount = Math.max(utgangspris - egenInsats, 0);
        const interestCost = (remainingAmount * mortgageInterestRate) / 12;
        
        // Calculate LTV and amortization
        const ltvRatio = utgangspris > 0 ? (remainingAmount / utgangspris) * 100 : 0;
        let requiredAmortizationRate = 0;
        if (ltvRatio > 70) {
            requiredAmortizationRate = 0.02;
        } else if (ltvRatio > 50) {
            requiredAmortizationRate = 0.01;
        }
        
        const calculatedAmortering = (remainingAmount * requiredAmortizationRate) / 12;
        
        // Determine final amortization
        let finalAmortering;
        if (amortizationTab === "percentage") {
            finalAmortering = (remainingAmount * parseFloat(amortizationPercentage)) / 100 / 12;
        } else {
            const userInput = parseFloat(formData.amortering);
            finalAmortering = !isNaN(userInput) && formData.amortering !== "" ? userInput : calculatedAmortering;
        }

        // One-time costs
        const nyaPantbrev = Math.max(remainingAmount - pantbrev, 0);
        const kostnadNyaPantbrev = nyaPantbrev > 0 ? nyaPantbrev * 0.02 + 375 : 0;
        const kostnadLagfart = Math.max(utgangspris, taxeringsvarde) * 0.015 + 825;
        const totalOneTimeCost = kostnadNyaPantbrev + kostnadLagfart;

        // Monthly costs
        const loanCost = interestCost + finalAmortering;
        const totalMonthlyCost = loanCost + monthlyCost;

        return {
            utgangspris,
            egenInsats,
            totalOneTimeCost,
            egenInsatsPlusOneTime: egenInsats + totalOneTimeCost,
            loanCost,
            totalMonthlyCost,
            difference: utgangspris - details.utgangspris
        };
    };

    // Generate 11 rows with selected interval around current price
    const currentPrice = details.utgangspris || 0;
    const startPrice = currentPrice - (5 * priceInterval); // 5 rows below current price
    const priceRows = [];

    for (let i = 0; i < 11; i++) {
        const price = startPrice + (i * priceInterval);
        if (price > 0) { // Only show positive prices
            priceRows.push(calculateForPrice(price));
        }
    }

    return (
        <Row style={{marginBottom: 8}} className="chart-row">
            <Col lg={12} md={12}>
                <Card>
                    <Card.Header style={{ backgroundColor: '#F5F7FA', borderBottom: '1px solid #E8EBF0', padding: '1rem' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <Card.Title className="mb-0" style={{ color: '#2c3e50', fontSize: '1.1rem', fontWeight: '600' }}>Prisjämförelse - Olika köpeskillingar</Card.Title>
                            <div className="d-flex align-items-center">
                                <Form.Label className="mb-0 me-2" style={{fontSize: '0.9rem', color: '#2c3e50'}}>Budintervall:</Form.Label>
                                <InputGroup style={{width: '120px'}}>
                                    <Form.Select 
                                        size="sm" 
                                        value={priceInterval} 
                                        onChange={(e) => setPriceInterval(parseInt(e.target.value))}
                                        style={{fontSize: '0.85rem'}}
                                    >
                                        <option value={5000}>5 000 kr</option>
                                        <option value={10000}>10 000 kr</option>
                                        <option value={25000}>25 000 kr</option>
                                        <option value={50000}>50 000 kr</option>
                                        <option value={100000}>100 000 kr</option>
                                    </Form.Select>
                                </InputGroup>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <p>Tabellen visar hur olika köpeskillingar påverkar dina kostnader. Aktuell köpeskilling är markerad.</p>

                        {/* Desktop Table View */}
                        <div className="d-none d-md-block">
                            <Table striped bordered hover responsive className="price-comparison-table">
                                <thead style={{ backgroundColor: '#003D7A', color: 'white' }}>
                                    <tr>
                                        <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Köpeskilling</th>
                                        <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Skillnad mot utgångspris</th>
                                        {formData.propertyType === "villa" && (
                                            <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Egen insats + Engång</th>
                                        )}
                                        <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Egen insats</th>
                                        {formData.propertyType === "villa" && (
                                            <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Engångskostnad</th>
                                        )}
                                        <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Totala lånekostnader<br/><small>(före skatteavdrag)</small></th>
                                        <th style={{ backgroundColor: '#003D7A', color: 'white' }}>Totala månadskostnaden<br/><small>{formData.propertyType === "bostadsratt" ? "med avgift" : "med driftkostnad"}</small></th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {priceRows.map((row, index) => {
                                        const isCurrentPrice = row.utgangspris === currentPrice;
                                        return (
                                            <tr key={index} style={isCurrentPrice ? {backgroundColor: 'rgba(74, 144, 226, 0.1)', fontWeight: 'bold', borderLeft: '3px solid #4A90E2'} : {}}>
                                                <td><strong>{formatCurrency(row.utgangspris)} kr</strong></td>
                                                <td>
                                                    <span style={{
                                                        backgroundColor: row.difference > 0 ? '#E74C3C' : row.difference < 0 ? '#27AE60' : 'transparent',
                                                        color: row.difference > 0 || row.difference < 0 ? 'white' : 'black',
                                                        padding: row.difference !== 0 ? '0.35rem 0.75rem' : '0',
                                                        borderRadius: '16px',
                                                        display: 'inline-block',
                                                        fontWeight: '600',
                                                        fontSize: '0.9rem'
                                                    }}>
                                                        {row.difference > 0 ? '+' : ''}{formatCurrency(row.difference)} kr
                                                    </span>
                                                </td>
                                                {formData.propertyType === "villa" && (
                                                    <td>{formatCurrency(row.egenInsatsPlusOneTime)} kr</td>
                                                )}
                                                <td>{formatCurrency(row.egenInsats)} kr</td>
                                                {formData.propertyType === "villa" && (
                                                    <td>{formatCurrency(row.totalOneTimeCost)} kr</td>
                                                )}
                                                <td>{formatCurrency(row.loanCost)} kr</td>
                                                <td>{formatCurrency(row.totalMonthlyCost)} kr</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {!isCurrentPrice && (
                                                        <Button
                                                            size="sm"
                                                            variant="link"
                                                            className="p-1"
                                                            onClick={() => applyRowSettings(row)}
                                                            title={`Använd inställningar: ${formatCurrency(row.utgangspris)} kr med ${formatCurrency(row.egenInsats)} kr kontantinsats`}
                                                            style={{
                                                                border: 'none',
                                                                boxShadow: 'none',
                                                                color: '#003D7A',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="17" y1="17" x2="7" y2="7"></line>
                                                                <line x1="7" y1="7" x2="7" y2="17"></line>
                                                                <line x1="7" y1="7" x2="17" y2="7"></line>
                                                            </svg>
                                                        </Button>
                                                    )}
                                                    {isCurrentPrice && (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="d-md-none">
                            {priceRows.map((row, index) => {
                                const isCurrentPrice = row.utgangspris === currentPrice;
                                return (
                                    <Card
                                        key={index}
                                        className="mb-3"
                                        style={{
                                            border: isCurrentPrice ? '2px solid #4A90E2' : '1px solid #E8EBF0',
                                            backgroundColor: isCurrentPrice ? 'rgba(74, 144, 226, 0.05)' : 'white',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Card.Body style={{ padding: '1rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                                <h6 style={{ marginBottom: 0, fontSize: '1rem', fontWeight: '700', color: '#003D7A' }}>
                                                    {formatCurrency(row.utgangspris)} kr
                                                </h6>
                                                <span style={{
                                                    backgroundColor: row.difference > 0 ? '#E74C3C' : row.difference < 0 ? '#27AE60' : 'transparent',
                                                    color: row.difference > 0 || row.difference < 0 ? 'white' : 'black',
                                                    padding: row.difference !== 0 ? '0.35rem 0.75rem' : '0',
                                                    borderRadius: '16px',
                                                    fontWeight: '600',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    {row.difference > 0 ? '+' : ''}{formatCurrency(row.difference)} kr
                                                </span>
                                            </div>

                                            {/* Cost Items */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                    <span style={{ color: '#6c757d' }}>Egen insats:</span>
                                                    <span style={{ fontWeight: '600', color: '#2c3e50' }}>{formatCurrency(row.egenInsats)} kr</span>
                                                </div>
                                                {formData.propertyType === "villa" && (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#6c757d' }}>Engångskostnad:</span>
                                                            <span style={{ fontWeight: '600', color: '#2c3e50' }}>{formatCurrency(row.totalOneTimeCost)} kr</span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#6c757d' }}>Egen insats + Engång:</span>
                                                            <span style={{ fontWeight: '600', color: '#2c3e50' }}>{formatCurrency(row.egenInsatsPlusOneTime)} kr</span>
                                                        </div>
                                                    </>
                                                )}
                                                <div style={{ borderTop: '1px solid #E8EBF0', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                        <span style={{ color: '#6c757d' }}>Totala lånekostnader:</span>
                                                        <span style={{ fontWeight: '600', color: '#2c3e50' }}>{formatCurrency(row.loanCost)} kr</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: '700', color: '#003D7A' }}>
                                                    <span>Totala månadskostnad:</span>
                                                    <span>{formatCurrency(row.totalMonthlyCost)} kr</span>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            {!isCurrentPrice && (
                                                <Button
                                                    className="w-100"
                                                    style={{
                                                        backgroundColor: '#003D7A',
                                                        border: 'none',
                                                        color: 'white',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '600',
                                                        padding: '0.5rem'
                                                    }}
                                                    onClick={() => applyRowSettings(row)}
                                                    title={`Använd inställningar: ${formatCurrency(row.utgangspris)} kr med ${formatCurrency(row.egenInsats)} kr kontantinsats`}
                                                >
                                                    Använd denna köpeskilling
                                                </Button>
                                            )}
                                            {isCurrentPrice && (
                                                <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', fontWeight: '600' }}>
                                                    ✓ Aktuell köpeskilling
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
}

PriceComparisonRow.propTypes = {
    formData: PropTypes.object.isRequired,
    details: PropTypes.object.isRequired,
    activeTab: PropTypes.string.isRequired,
    percentageValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    amortizationTab: PropTypes.string.isRequired,
    amortizationPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    setFormData: PropTypes.func.isRequired,
    setActiveTab: PropTypes.func.isRequired,
    setPercentageValue: PropTypes.func.isRequired,
    setIsEgenInsatsManuallyEdited: PropTypes.func.isRequired
};

export default PriceComparisonRow;